import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { azn } from "@/lib/api";
import { Sprout, LayoutDashboard, Store, Wallet, ShieldCheck, LogOut, Menu, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";

const links = [
  { to: "/dashboard", label: "İdarə Paneli", icon: LayoutDashboard },
  { to: "/marketplace", label: "Ağac Bazarı", icon: Store },
  { to: "/wheel", label: "Çarx", icon: Sparkles },
  { to: "/wallet", label: "Cüzdan", icon: Wallet },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = user?.role === "admin"
    ? [...links, { to: "/admin", label: "Admin", icon: ShieldCheck }]
    : links;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
          <span className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
            <Sprout className="w-5 h-5 text-primary-foreground" />
          </span>
          <span className="font-heading font-bold text-lg tracking-tight">Yaşıl İnvest</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {nav.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.slice(1)}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                  active ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <l.icon className="w-4 h-4" /> {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex flex-col items-end leading-tight">
            <span className="text-xs text-muted-foreground">Balans</span>
            <span className="font-heading font-bold text-primary" data-testid="navbar-balance">{azn(user?.balance)}</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-9 h-9 rounded-full bg-primary text-primary-foreground font-semibold flex items-center justify-center" data-testid="navbar-avatar">
                {initials}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-sm">
                <p className="font-medium truncate">{user?.name || "İstifadəçi"}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" /> Çıxış
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-border/50 px-4 py-3 flex flex-col gap-1">
          {nav.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-muted"
              data-testid={`mobile-nav-${l.to.slice(1)}`}
            >
              <l.icon className="w-4 h-4" /> {l.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}
